import React, { useState, useEffect } from 'react'
import {
  T, tierFromProb, pct, ScrollRow, SearchBar, Spinner, Empty, Stat,
  SectionLabel, probColor, BottomNav,
} from '../nfl/ui'
import { PickCard, GameCard, PlayRow, matchupBucket } from './cards'
import { PlayerModal } from './modals'
import { ParlayTab } from './parlay'
import { MoreTab, GlossarySheet } from './glossary'
import {
  getCFBStatus, getCFBLeaderboard, getCFBScorecard, getCFBValidation,
  getCFBTop, getCFBGames, getCFBSignals,
} from './api'

// College-football app — the NFL app's chrome and layout (sticky header, week banner, icon
// bottom nav: This Week · Board · Touchdowns · Games · Parlay · More, Record at the top of
// More), reusing the NFL design system (../nfl/ui) so it's visually identical.
// Isolated: reads only /cfb/* endpoints. No fabricated data — an honest empty state
// until the feed lands.

const PROPS = [
  ['', 'All'], ['anytime_td', 'Anytime TD'], ['receptions', 'Receptions'],
  ['rush_yards', 'Rush Yds'], ['rec_yards', 'Rec Yds'], ['pass_yards', 'Pass Yds'],
]
const PROP_LABEL = { anytime_td: 'Anytime TD', receptions: 'Receptions',
  rush_yards: 'Rushing Yards', rec_yards: 'Receiving Yards', pass_yards: 'Passing Yards' }
const WEEKS = Array.from({ length: 15 }, (_, i) => i + 1)

// ── Header — the NFL top nav: sticky, wordmark + sport badge, glossary, back link ──
function Header({ onGlossary }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px',
      borderBottom: `1px solid ${T.border}`, background: '#0D1117', position: 'sticky', top: 0, zIndex: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontWeight: 900, fontSize: 22, letterSpacing: '-0.04em' }}>
          <span style={{ color: T.text }}>LINE</span><span style={{ color: T.green }}>BRKR</span>
        </div>
        <span style={{ fontWeight: 800, fontSize: 11, color: '#04140D', background: T.green, borderRadius: 5,
          padding: '2px 6px', letterSpacing: '0.06em' }}>CFB</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button onClick={onGlossary} aria-label="What the numbers mean"
          style={{ background: 'none', border: `1px solid ${T.border2}`, cursor: 'pointer', color: T.muted,
            borderRadius: 999, width: 22, height: 22, fontSize: 12, fontWeight: 800, lineHeight: 1, padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>?</button>
        <a href="?" style={{ fontSize: 11, color: T.muted, textDecoration: 'none' }}>‹ All sports</a>
      </div>
    </div>
  )
}

// ── Week banner — the NFL current-week bar. Opens on the live week; the small stepper
// replaces the old W1–W15 chip rows so past weeks are still a tap away.
function WeekBanner({ season, week, liveWeek, setWeek }) {
  const step = d => setWeek(Math.min(WEEKS.length, Math.max(1, week + d)))
  const arrow = (d, label) => (
    <button onClick={() => step(d)} aria-label={label} disabled={d < 0 ? week <= 1 : week >= WEEKS.length}
      style={{ background: T.card2, border: `1px solid ${T.border2}`, color: T.muted, borderRadius: 999,
        width: 24, height: 24, padding: 0, fontSize: 13, fontWeight: 800, lineHeight: 1, cursor: 'pointer' }}>{d < 0 ? '‹' : '›'}</button>
  )
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 16px',
      borderBottom: `1px solid ${T.border}`, background: '#0B1018' }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: T.text }}>Week {week}
        <span style={{ fontSize: 11, fontWeight: 600, color: T.muted, marginLeft: 6 }}>· {season} season</span>
        {liveWeek && week !== liveWeek && (
          <button onClick={() => setWeek(liveWeek)} style={{ marginLeft: 8, background: 'none', border: 'none',
            color: T.green, fontSize: 11, fontWeight: 800, cursor: 'pointer', padding: 0 }}>Back to this week</button>
        )}
      </span>
      <span style={{ display: 'inline-flex', gap: 6 }}>{arrow(-1, 'Previous week')}{arrow(1, 'Next week')}</span>
    </div>
  )
}

// ── Chip ────────────────────────────────────────────────────────────────────
function Chip({ on, onClick, children }) {
  return (
    <button onClick={onClick} style={{ flexShrink: 0, cursor: 'pointer', fontSize: 12, fontWeight: 700,
      padding: '6px 11px', borderRadius: 8, border: `1px solid ${on ? T.green : T.border}`,
      background: on ? T.green : T.card2, color: on ? '#04140D' : T.muted }}>{children}</button>
  )
}

// ── Home tab ──────────────────────────────────────────────────────────────────
// ── Week hero — the time-aware top strip (mirrors the NFL TodayStrip pre-face):
// "Week N · <day>", game count, a countdown to the next kickoff + a progress bar.
function WeekHero({ week, games }) {
  const gs = games || []
  const kt = g => (g.gametime ? Date.parse(g.gametime) : Infinity)
  const now = Date.now()
  const upcoming = gs.filter(g => isFinite(kt(g)) && kt(g) > now).sort((a, b) => kt(a) - kt(b))
  const nx = upcoming[0]
  const nxDay = nx ? (nx.day || '') : ''
  const sameDay = nx ? upcoming.filter(g => g.day === nx.day).length : 0
  const fmt = ms => {
    if (!isFinite(ms) || ms <= 0) return null
    const h = Math.floor(ms / 3.6e6), m = Math.floor((ms % 3.6e6) / 6e4)
    return h >= 1 ? `${h}h ${m}m` : `${m}m`
  }
  const countdown = nx ? fmt(kt(nx) - now) : null
  const pctToKick = nx ? Math.max(3, Math.min(100, 100 - ((kt(nx) - now) / (72 * 3.6e6)) * 100)) : 0
  return (
    <div style={{ background: 'linear-gradient(120deg,#0c2418,#0b1119)', border: `1px solid ${T.greenMid}`,
      borderRadius: 13, padding: '12px 14px', marginBottom: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 900 }}>Week {week}{nxDay ? ` · ${nxDay}` : ''}</div>
      <div style={{ fontSize: 11.5, color: T.muted, marginTop: 3 }}>
        {gs.length} game{gs.length !== 1 ? 's' : ''}
        {nx && countdown ? <> · {sameDay > 1 ? `${sameDay} games ` : 'next '}kick{sameDay > 1 ? 'off' : 's'} in <b style={{ color: T.muted2 }}>{countdown}</b></> : ''}
      </div>
      {nx && countdown && (
        <div style={{ height: 4, background: '#0a1a12', borderRadius: 3, marginTop: 9, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pctToKick}%`, background: T.green, borderRadius: 3 }} />
        </div>
      )}
    </div>
  )
}

// ── Trust bar — the front-page proof, on the way to the plays (mirrors NFL). Leads with
// the live season record when there is one, else the backtested calibration note. Taps
// into the Record tab.
function TrustBar({ season, setView }) {
  const [card, setCard] = useState(null)
  useEffect(() => { let a = true; getCFBScorecard(season, '', 0, 'STRONG').then(d => { if (a) setCard(d) }); return () => { a = false } }, [season])
  const o = card?.overall || {}
  const graded = o.graded || 0
  let txt
  if (graded >= 10 && o.win_pct != null) {
    txt = <>High-conviction plays are <b style={{ color: T.green, fontFamily: T.mono }}>{o.wins}-{o.losses}</b> ({pct(o.win_pct)}) this season</>
  } else {
    txt = <>Model backtested 2022–25 · rush & rec yards calibrated — see the full record</>
  }
  return (
    <button onClick={() => setView && setView('record')} style={{ display: 'flex', width: '100%', alignItems: 'center',
      justifyContent: 'space-between', gap: 8, cursor: 'pointer', textAlign: 'left', color: T.text,
      background: T.card3, border: '1px solid #113726', borderRadius: 11, padding: '10px 12px', marginBottom: 12 }}>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 9.5, fontWeight: 800, letterSpacing: '0.1em', color: T.green, marginBottom: 2 }}>✓ CALIBRATED · BACKTESTED</span>
        <span style={{ fontSize: 11.5, color: T.muted2, lineHeight: 1.4 }}>{txt}</span>
      </span>
      <span style={{ color: T.green, fontWeight: 900, flexShrink: 0 }}>→</span>
    </button>
  )
}

// ── Hero read — the model's single best play (mirrors NFL HeroRead). Big hit%, the bet,
// an optional edge chip, and a "why this read" tap into the full breakdown.
function HeroRead({ p, onPlayer }) {
  if (!p) return null
  const isTD = p.prop === 'anytime_td'
  const bet = isTD ? 'Anytime TD' : `${p.market} Over ${p.line}`
  const meta = [p.pos, p.team, p.opp ? `vs ${p.opp}` : ''].filter(Boolean).join(' · ')
  const edge = p.edge_pct
  return (
    <button onClick={() => onPlayer && onPlayer(p)} style={{ display: 'block', width: '100%', textAlign: 'left',
      cursor: 'pointer', background: T.card, border: `1px solid ${T.greenMid}`, borderRadius: 14, padding: 14, marginBottom: 12, color: T.text }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7 }}>
            <span style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 13, color: T.green }}>#1</span>
            <span style={{ fontSize: 16.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
          </div>
          <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1 }}>{meta}</div>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: T.text, background: T.card2,
            border: `1px solid ${T.border}`, borderRadius: 6, padding: '3px 8px', display: 'inline-block', marginTop: 8 }}>{bet}</span>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 30, lineHeight: 1, color: probColor(p.prob) }}>{pct(p.prob)}</div>
          <div style={{ fontSize: 8.5, color: T.muted, letterSpacing: '0.04em', marginTop: 3 }}>{isTD ? 'TO SCORE' : 'TO HIT'}</div>
        </div>
      </div>
      {edge != null && !p.line_suspect && Math.abs(edge) >= 1 && (
        <div style={{ marginTop: 10 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 7, padding: '4px 8px',
            color: edge >= 0 ? T.green : T.red, background: edge >= 0 ? '#0a2018' : T.redDim,
            border: `1px solid ${edge >= 0 ? '#0c3d2a' : '#3d1414'}` }}>
            {edge >= 0 ? '+' : ''}{edge.toFixed(1)}% edge vs the book
          </span>
        </div>
      )}
      <div style={{ marginTop: 11, textAlign: 'center', fontSize: 11.5, fontWeight: 800, color: T.green,
        border: `1px solid ${T.greenMid}`, borderRadius: 9, padding: '8px 0' }}>Why this read →</div>
    </button>
  )
}

// ── Market survey — every market at a glance (mirrors NFL MarketSurvey): best play + how
// many plays, tap a row to deep-link into that market on the board.
function MarketSurvey({ markets, onMarket, onPlayer }) {
  if (!markets || !markets.length) return null
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
      {markets.map((mk, i) => {
        const b = mk.best || {}
        return (
          <button key={mk.key} onClick={() => onMarket && onMarket(mk.key)} style={{ display: 'flex', width: '100%', textAlign: 'left',
            alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '11px 14px', border: 'none',
            borderTop: i ? `1px solid ${T.border}` : 'none', background: 'transparent', cursor: 'pointer', color: T.text }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                {mk.label}<span style={{ fontSize: 11, color: T.muted, fontWeight: 700 }}>{mk.n} play{mk.n !== 1 ? 's' : ''}</span>
              </div>
              {b.name && <div style={{ fontSize: 11, color: T.muted, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.name} · {b.team} {b.opp ? `vs ${b.opp}` : ''}</div>}
            </div>
            {b.name && (
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: T.mono, fontWeight: 800, color: probColor(b.prob) }}>{pct(b.prob)}</div>
                <div style={{ fontSize: 10, color: T.muted }}>{mk.key === 'anytime_td' ? 'to score' : 'to hit'}</div>
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

// ── Home "angle" signal cards (data we already have) ───────────────────────────
function SigPlay({ p, onPlayer, accent }) {
  const isTD = p.prop === 'anytime_td'
  const bet = isTD ? 'Anytime TD' : (p.book_line != null ? `${p.market} O${p.book_line}` : p.market)
  return (
    <button onClick={() => onPlayer && onPlayer(p)} style={{ display: 'flex', width: '100%', alignItems: 'center',
      justifyContent: 'space-between', gap: 8, background: T.card2, border: 'none', borderRadius: 8,
      padding: '8px 10px', cursor: 'pointer', color: T.text, textAlign: 'left' }}>
      <span style={{ minWidth: 0, overflow: 'hidden' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
          {p.name} <span style={{ color: T.muted, fontWeight: 500 }}>{p.pos} · {p.team}{p.opp ? ` vs ${p.opp}` : ''}</span></span>
        <span style={{ fontSize: 11, color: T.muted2 }}>{bet}</span>
        {p.matchup && <span style={{ fontSize: 10.5, color: accent || T.green, display: 'block', marginTop: 1 }}>◆ {p.matchup}</span>}
      </span>
      <span style={{ fontFamily: T.mono, fontWeight: 800, color: probColor(p.prob || 0), flexShrink: 0 }}>{pct(p.prob || 0)}</span>
    </button>
  )
}

function SignalCard({ icon, title, accent, subtitle, plays, onPlayer }) {
  if (!plays || !plays.length) return null
  return (
    <div style={{ background: T.card, border: `1px solid ${accent}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: accent, letterSpacing: '0.03em', marginBottom: 5 }}>{icon} {title}</div>
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 9, lineHeight: 1.4 }}>{subtitle}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {plays.map((p, i) => <SigPlay key={i} p={p} onPlayer={onPlayer} accent={accent} />)}
      </div>
    </div>
  )
}

function ShootoutCard({ sh, onPlayer }) {
  if (!sh) return null
  return (
    <div style={{ background: T.card, border: `1px solid ${T.amber}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: T.amber, letterSpacing: '0.03em' }}>🌪️ SHOOTOUT</span>
        <span style={{ fontSize: 11, color: T.muted }}>highest total on the board</span>
      </div>
      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 2 }}>
        {sh.away} @ {sh.home}
        <span style={{ fontSize: 13, color: T.amber, fontWeight: 800, marginLeft: 8, fontFamily: T.mono }}>O/U {sh.total}</span>
      </div>
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 9, lineHeight: 1.4 }}>
        The game where overs stack — passing, receiving and TD scoring all get a lift. Research only.</div>
      {sh.overs && sh.overs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sh.overs.map((p, i) => <SigPlay key={i} p={p} onPlayer={onPlayer} accent={T.amber} />)}
        </div>
      )}
    </div>
  )
}

function StackCard({ st, onPlayer }) {
  if (!st) return null
  return (
    <div style={{ background: T.card, border: `1px solid ${T.greenMid}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ fontSize: 12.5, fontWeight: 800, color: T.green, letterSpacing: '0.03em', marginBottom: 5 }}>🎯 SAME-GAME STACK</div>
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 9, lineHeight: 1.4 }}>
        {st.qb.team} in {st.away} @ {st.home} (O/U {st.total}) — a QB and his top target move together. Research only.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SigPlay p={st.qb} onPlayer={onPlayer} />
        <div style={{ textAlign: 'center', fontSize: 10, color: T.muted, letterSpacing: '0.1em' }}>+</div>
        <SigPlay p={st.rec} onPlayer={onPlayer} />
      </div>
    </div>
  )
}

function Signals({ season, week, onPlayer }) {
  const [sig, setSig] = useState(null)
  useEffect(() => { let a = true; getCFBSignals(season, week).then(r => { if (a) setSig(r) }); return () => { a = false } }, [season, week])
  if (!sig) return null
  const has = (sig.td_storm && sig.td_storm.length) || sig.shootout || sig.stack
    || (sig.air_raid && sig.air_raid.length) || (sig.ground_control && sig.ground_control.length)
  if (!has) return null
  return (
    <>
      <SectionLabel>⚡ Signals</SectionLabel>
      <SignalCard icon="🌩️" title="TD STORM" accent={T.orange || T.amber} onPlayer={onPlayer}
        subtitle="Anytime-TD spots where it lines up — the model likes him to score, his offense is expected to, and the D is soft to his TDs."
        plays={sig.td_storm} />
      <ShootoutCard sh={sig.shootout} onPlayer={onPlayer} />
      <SignalCard icon="🏈" title="AIR RAID" accent={T.purple || T.green} onPlayer={onPlayer}
        subtitle="Pass-catchers in throw-heavy spots — underdogs and high totals, where receiving volume inflates."
        plays={sig.air_raid} />
      <SignalCard icon="🐢" title="GROUND CONTROL" accent={T.amber} onPlayer={onPlayer}
        subtitle="Backfields in positive scripts — favorites who grind out the clock on the ground."
        plays={sig.ground_control} />
      <StackCard st={sig.stack} onPlayer={onPlayer} />
    </>
  )
}

// Home — the command center, NFL-style: the week hero + proof, then the model's top read,
// the high-conviction plays, value, every market at a glance, and a games preview. The
// full depth still lives one tap away on the Board / Games tabs.
function HomeTab({ season, week, onPlayer, setView, onMarket }) {
  const [data, setData] = useState(null)
  const [games, setGames] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let live = true; setLoading(true)
    Promise.all([getCFBTop(season, week, 12), getCFBGames(season, week)]).then(([d, g]) => {
      if (live) { setData(d); setGames(g.games || []); setLoading(false) }
    })
    return () => { live = false }
  }, [season, week])
  const top = data?.top || []
  const value = data?.value || []
  const byMarket = data?.by_market || []
  const tdOutlook = data?.td_outlook || 0
  const c = data?.counts || {}
  // High-conviction strip — ELITE / BEST BET / STRONG across every market, the model's
  // best plays in one glance (same tiering the cards badge with).
  const HIGH = new Set(['ELITE', 'BEST BET', 'STRONG'])
  const hero = top[0]
  const conviction = top.slice(1).filter(p => HIGH.has(tierFromProb(p.prob, p.confidence))).slice(0, 6)
  return (
    <div>
      {loading ? <Spinner />
        : top.length === 0 ? <Empty message={`No plays for week ${week} yet.`} />
        : <>
          <WeekHero week={week} games={games} />
          <TrustBar season={season} setView={setView} />
          {tdOutlook > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: T.card, border: `1px solid ${T.border2}`,
              borderRadius: 12, padding: '12px 14px', marginBottom: 12 }}>
              <span style={{ fontSize: 22 }}>🏈</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>~{Math.round(tdOutlook)} touchdowns projected this week</div>
                <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1 }}>across {c.games || 0} games · {c.plays || 0} plays on the board</div>
              </div>
            </div>
          )}

          <SectionLabel>The model's top read</SectionLabel>
          <HeroRead p={hero} onPlayer={onPlayer} />

          {conviction.length > 0 && <>
            <SectionLabel>⭐ High-conviction plays</SectionLabel>
            <div style={{ background: T.card, border: `1px solid ${T.greenMid}`, borderRadius: 12, padding: '4px 12px 8px', marginBottom: 12 }}>
              {conviction.map((p, i) => <PlayRow key={i} p={p} onTap={onPlayer} border={i > 0} />)}
            </div>
          </>}

          <Signals season={season} week={week} onPlayer={onPlayer} />

          {value.length > 0 && <>
            <SectionLabel>💰 Value plays</SectionLabel>
            <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '4px 12px 8px', marginBottom: 12 }}>
              {value.slice(0, 5).map((p, i) => <PlayRow key={i} p={p} onTap={onPlayer} border={i > 0} />)}
            </div>
          </>}

          <SectionLabel>Every market at a glance</SectionLabel>
          <div style={{ marginBottom: 12 }}>
            <MarketSurvey markets={byMarket} onMarket={onMarket} onPlayer={onPlayer} />
          </div>

          {games && games.length > 0 && <>
            <SectionLabel right="All games →" onRight={() => setView && setView('games')}>This week's games</SectionLabel>
            {games.slice(0, 3).map((g, i) => <GameCard key={i} g={g} onTap={onPlayer} />)}
          </>}

          <button onClick={() => setView && setView('board')} style={{ width: '100%', padding: '11px', marginTop: 2, borderRadius: 10,
            border: `1px solid ${T.border}`, background: T.card2, color: T.text, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
            See the full board →
          </button>
        </>}
      <div style={{ height: 16 }} />
    </div>
  )
}

// ── Board tab ──────────────────────────────────────────────────────────────────
function BoardTab({ season, week, onPlayer, initialProp = '', onInit }) {
  const [prop, setProp] = useState(initialProp)
  const [sort, setSort] = useState('prob')
  // A market preselected from Home is a one-shot: clear it in the parent so the Board
  // opens unfiltered next time it's reached via the nav.
  useEffect(() => { if (initialProp && onInit) onInit() }, [])
  const [matchup, setMatchup] = useState('')
  const [pos, setPos] = useState('')
  const [day, setDay] = useState('')
  const [q, setQ] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let live = true; setLoading(true)
    // All filters run server-side across the WHOLE week's slate (not just the loaded
    // page), so a lower-volume group like TEs still surfaces. Debounced for typing.
    const t = setTimeout(() => {
      getCFBLeaderboard(season, week, prop, 150, sort, q.trim(), pos, matchup, day)
        .then(d => { if (live) { setData(d); setLoading(false) } })
    }, q.trim() ? 250 : 0)
    return () => { live = false; clearTimeout(t) }
  }, [season, week, prop, sort, q, pos, matchup, day])
  // Game days differ per week, so a day pick doesn't carry across weeks.
  useEffect(() => { setDay('') }, [week])
  const plays = data?.plays || []
  const MATCHUPS = ['Great', 'Good', 'Neutral', 'Tough']
  const POSITIONS = ['QB', 'RB', 'WR', 'TE']
  // Best Bets — a curated strip on the default (unfiltered) view. Ranks real,
  // non-suspect value first, then the markets the backtest trusts most (rush/rec
  // yards), then hit%. Hidden the moment the user starts filtering/searching.
  const TRUST = new Set(['rush_yards', 'rec_yards'])
  const bbScore = p => (p.value ? 1000 : 0) + (TRUST.has(p.prop) ? 120 : 0)
    + (p.edge_pct || 0) * 8 + (p.prob || 0) * 100
  const DAYS = data?.days || []
  const DAY_FULL = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' }
  const isDefault = prop === '' && !q && !matchup && !pos && !day
  const bestBets = isDefault
    ? [...plays].filter(p => !p.line_suspect).sort((a, b) => bbScore(b) - bbScore(a)).slice(0, 3)
    : []
  const bestKeys = new Set(bestBets.map(p => `${p.cfbd_id}|${p.prop}`))
  const rest = plays.filter(p => !bestKeys.has(`${p.cfbd_id}|${p.prop}`))
  const rawCount = data?.total ?? plays.length
  const anyFilter = !!(prop || pos || matchup || q || day)
  const nFilters = [pos, matchup, day].filter(Boolean).length + (sort !== 'prob' ? 1 : 0)
  const clearFilters = () => { setProp(''); setPos(''); setMatchup(''); setQ(''); setDay(''); setSort('prob') }
  const activeLabels = [
    prop && (PROPS.find(([k]) => k === prop) || [, prop])[1],
    pos && `${pos} only`, matchup && `${matchup} matchup`,
    day && (DAY_FULL[day] || day), q && `“${q}”`,
  ].filter(Boolean).join(' · ')
  return (
    <div>
      <ScrollRow style={{ marginBottom: 10 }}>
        {PROPS.map(([k, label]) => <Chip key={k} on={prop === k} onClick={() => setProp(k)}>{label}</Chip>)}
      </ScrollRow>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <SearchBar value={q} onChange={setQ} placeholder="Search player, team, or opponent…" />
        </div>
        <button onClick={() => setShowFilters(v => !v)} aria-expanded={showFilters} style={{ flexShrink: 0, cursor: 'pointer',
          fontSize: 12, fontWeight: 800, padding: '9px 12px', borderRadius: 9,
          border: `1px solid ${nFilters || showFilters ? T.green : T.border}`, background: T.card,
          color: nFilters ? T.green : T.muted }}>Filters{nFilters ? ` · ${nFilters}` : ''}</button>
      </div>
      {showFilters && (
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '10px 10px 2px', marginBottom: 10 }}>
          <ScrollRow style={{ marginBottom: 8 }}>
            <span style={{ flexShrink: 0, alignSelf: 'center', fontSize: 11, color: T.faint, marginRight: 2, width: 52 }}>Position</span>
            <Chip on={pos === ''} onClick={() => setPos('')}>All</Chip>
            {POSITIONS.map(pk => <Chip key={pk} on={pos === pk} onClick={() => setPos(pk)}>{pk}</Chip>)}
          </ScrollRow>
          <ScrollRow style={{ marginBottom: 8 }}>
            <span style={{ flexShrink: 0, alignSelf: 'center', fontSize: 11, color: T.faint, marginRight: 2, width: 52 }}>Matchup</span>
            <Chip on={matchup === ''} onClick={() => setMatchup('')}>All</Chip>
            {MATCHUPS.map(mk => <Chip key={mk} on={matchup === mk} onClick={() => setMatchup(mk)}>{mk}</Chip>)}
          </ScrollRow>
          {DAYS.length > 1 && (
            <ScrollRow style={{ marginBottom: 8 }}>
              <span style={{ flexShrink: 0, alignSelf: 'center', fontSize: 11, color: T.faint, marginRight: 2, width: 52 }}>Day</span>
              <Chip on={day === ''} onClick={() => setDay('')}>All</Chip>
              {DAYS.map(d => <Chip key={d} on={day === d} onClick={() => setDay(d)}>{d}</Chip>)}
            </ScrollRow>
          )}
          <ScrollRow style={{ marginBottom: 8 }}>
            <span style={{ flexShrink: 0, alignSelf: 'center', fontSize: 11, color: T.faint, marginRight: 2, width: 52 }}>Sort</span>
            <Chip on={sort === 'prob'} onClick={() => setSort('prob')}>Model %</Chip>
            <Chip on={sort === 'edge'} onClick={() => setSort('edge')}>Edge</Chip>
          </ScrollRow>
        </div>
      )}
      {anyFilter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 2px 10px' }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: T.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activeLabels}</span>
          <button onClick={clearFilters} style={{ flexShrink: 0, cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
            padding: '5px 10px', borderRadius: 8, border: `1px solid ${T.amber}`, background: 'transparent', color: T.amber }}>✕ Clear</button>
        </div>
      )}
      {loading ? <Spinner />
        : plays.length === 0
          ? (rawCount > 0 && anyFilter
            ? <div style={{ textAlign: 'center', padding: '28px 16px' }}>
                <div style={{ fontSize: 30, marginBottom: 6 }}>🔍</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>No plays match these filters</div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 4, lineHeight: 1.4 }}>{activeLabels}</div>
                <div style={{ fontSize: 11.5, color: T.faint, marginTop: 8 }}>
                  {rawCount} plays this week — your filters are narrowing them all out.
                </div>
                <button onClick={clearFilters} style={{ marginTop: 14, padding: '9px 16px', borderRadius: 10,
                  border: `1px solid ${T.green}`, background: T.green, color: '#04140D', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                  Clear filters
                </button>
              </div>
            : <Empty message={`No plays for week ${week} yet.`} />)
        : <>
          {bestBets.length > 0 && <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '2px 2px 8px' }}>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: T.green }}>⭐ Best bets</span>
              <span style={{ fontSize: 10.5, color: T.faint }}>this week · model-trusted, non-suspect</span>
            </div>
            {bestBets.map((p, i) => <PickCard key={`bb${i}`} p={p} rank={i + 1} onTap={onPlayer} />)}
            <div style={{ fontSize: 11, fontWeight: 700, color: T.faint, textTransform: 'uppercase', letterSpacing: '.05em', margin: '14px 2px 8px' }}>All plays</div>
          </>}
          {rest.map((p, i) => <PickCard key={i} p={p} rank={bestBets.length ? 99 : i + 1} onTap={onPlayer} />)}
        </>}
      <div style={{ height: 16 }} />
    </div>
  )
}

// ── Touchdowns tab — the NFL Touchdowns door: who scores this week. TD Storm (the spots
// where the score lines up) on top, then every anytime-TD look, model-ranked.
function TouchdownsTab({ season, week, onPlayer }) {
  const [q, setQ] = useState('')
  const [data, setData] = useState(null)
  const [storm, setStorm] = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let live = true; setLoading(true)
    const t = setTimeout(() => {
      getCFBLeaderboard(season, week, 'anytime_td', 100, 'prob', q.trim())
        .then(d => { if (live) { setData(d); setLoading(false) } })
    }, q.trim() ? 250 : 0)
    return () => { live = false; clearTimeout(t) }
  }, [season, week, q])
  useEffect(() => {
    let a = true; getCFBSignals(season, week).then(r => { if (a) setStorm(r?.td_storm || []) })
    return () => { a = false }
  }, [season, week])
  const plays = data?.plays || []
  return (
    <div>
      <div style={{ fontSize: 12, color: T.muted, margin: '2px 2px 12px', lineHeight: 1.45 }}>
        Who scores this week — every anytime-TD look, ranked by the model's chance to find the end zone.
      </div>
      {!q && <SignalCard icon="🌩️" title="TD STORM" accent={T.orange || T.amber} onPlayer={onPlayer}
        subtitle="Where it lines up — the model likes him to score, his offense is expected to, and the D is soft to his TDs."
        plays={storm} />}
      <SearchBar value={q} onChange={setQ} placeholder="Search player, team, or opponent…" />
      {loading ? <Spinner />
        : plays.length === 0
          ? <Empty message={q ? 'No scorers match your search.' : `No anytime-TD plays for week ${week} yet.`} />
          : <>
            {!q && <SectionLabel>Most likely to score</SectionLabel>}
            {plays.map((p, i) => <PickCard key={i} p={p} rank={i + 1} onTap={onPlayer} />)}
          </>}
      <div style={{ height: 16 }} />
    </div>
  )
}

// ── Games tab ──────────────────────────────────────────────────────────────────
function GamesTab({ season, week, onPlayer }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [day, setDay] = useState('')
  useEffect(() => {
    let live = true; setLoading(true)
    getCFBGames(season, week).then(d => { if (live) { setData(d); setLoading(false) } })
    return () => { live = false }
  }, [season, week])
  // Game days differ per week, so a day pick doesn't carry across weeks.
  useEffect(() => { setDay('') }, [week])
  const all = data?.games || []
  const DAYS = data?.days || []
  const ql = q.trim().toLowerCase()
  // Intuitive search: team OR conference OR any player in the game. Plus an optional
  // day filter (kickoff weekday, ET — same convention as the board).
  const games = all.filter(g =>
    (!day || g.day === day)
    && (!ql
      || [g.home, g.away, g.home_conference, g.away_conference].some(x => (x || '').toLowerCase().includes(ql))
      || (g.plays || []).some(p => (p.name || '').toLowerCase().includes(ql))))
  return (
    <div>
      {DAYS.length > 1 && (
        <ScrollRow style={{ marginBottom: 10 }}>
          <span style={{ flexShrink: 0, alignSelf: 'center', fontSize: 11, color: T.faint, marginRight: 2 }}>Day</span>
          <Chip on={day === ''} onClick={() => setDay('')}>All</Chip>
          {DAYS.map(d => <Chip key={d} on={day === d} onClick={() => setDay(d)}>{d}</Chip>)}
        </ScrollRow>
      )}
      <SearchBar value={q} onChange={setQ} placeholder="Search team, conference, or player…" />
      {loading ? <Spinner />
        : games.length === 0
          ? <Empty message={all.length && (ql || day) ? `No games match your filters.` : `No games for week ${week} yet.`} />
        : games.map((g, i) => <GameCard key={i} g={g} onTap={onPlayer} />)}
      <div style={{ height: 16 }} />
    </div>
  )
}

// ── Record tab ─────────────────────────────────────────────────────────────────
const REC_MARKETS = [['anytime_td', 'Anytime TD'], ['receptions', 'Receptions'],
  ['rush_yards', 'Rush Yds'], ['rec_yards', 'Rec Yds'], ['pass_yards', 'Pass Yds']]
const TRUSTED_MARKETS = ['rush_yards', 'rec_yards']

function RecordTab({ season }) {
  const [card, setCard] = useState(null)
  const [val, setVal] = useState(null)
  const [sel, setSel] = useState([])      // selected markets (empty = all)
  const [tier, setTier] = useState('STRONG')   // default: NFL-style high-conviction record
  const selKey = sel.slice().sort().join(',')
  useEffect(() => {
    getCFBScorecard(season, selKey, 0, tier).then(setCard)
  }, [season, selKey, tier])
  useEffect(() => { getCFBValidation().then(setVal) }, [season])
  const toggle = k => setSel(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k])
  const o = card?.overall || {}
  const wl = o.graded ? `${o.wins || 0}-${o.losses || 0}${o.pushes ? `-${o.pushes}` : ''}` : '—'
  const byProp = card?.by_prop || {}
  const markets = val?.markets || {}
  const VC = { PASS: T.green, REVIEW: T.amber, BUILDING: T.faint }
  const trustedOn = TRUSTED_MARKETS.every(m => sel.includes(m)) && sel.length === TRUSTED_MARKETS.length
  return (
    <div>
      <SectionLabel>{season} record</SectionLabel>
      {/* Scope — filter what counts in the record (grading is unchanged underneath) */}
      <ScrollRow style={{ marginBottom: 8 }}>
        <Chip on={sel.length === 0} onClick={() => setSel([])}>All markets</Chip>
        <Chip on={trustedOn} onClick={() => setSel(trustedOn ? [] : [...TRUSTED_MARKETS])}>✓ Trusted</Chip>
        {REC_MARKETS.map(([k, label]) => <Chip key={k} on={sel.includes(k)} onClick={() => toggle(k)}>{label}</Chip>)}
      </ScrollRow>
      <ScrollRow style={{ marginBottom: 4 }}>
        <span style={{ flexShrink: 0, alignSelf: 'center', fontSize: 11, color: T.faint, marginRight: 2 }}>Conviction</span>
        {[['', 'All plays'], ['STRONG', 'Strong+'], ['BEST BET', 'Best bet+'], ['ELITE', 'Elite']].map(([v, label]) =>
          <Chip key={v || 'all'} on={tier === v} onClick={() => setTier(v)}>{label}</Chip>)}
      </ScrollRow>
      <div style={{ fontSize: 10.5, color: T.faint, margin: '0 2px 10px', lineHeight: 1.4 }}>
        Record counts only {tier ? `${tier === 'STRONG' ? 'Strong+' : tier === 'BEST BET' ? 'Best bet+' : 'Elite'} conviction plays` : 'every projection'} — like the NFL board. Grading is unchanged underneath.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
        <Stat label="Record" value={wl} />
        <Stat label="Hit %" value={o.win_pct != null ? pct(o.win_pct) : '—'} color={T.green} />
        <Stat label="Graded" value={o.graded || 0} />
      </div>
      {Object.keys(card?.by_week || {}).length > 0 && (
        <>
          <SectionLabel>By week</SectionLabel>
          {Object.entries(card.by_week)
            .sort((a, b) => Number(b[0]) - Number(a[0]))
            .map(([wk, v]) => (
            <div key={wk} style={{ display: 'flex', alignItems: 'center', padding: '9px 2px', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ flex: 1, fontSize: 13, color: T.text }}>Week {wk}
                <span style={{ fontSize: 10, color: T.faint }}> · {v.graded || 0} plays</span></span>
              <span style={{ width: 70, textAlign: 'right', fontFamily: T.mono, fontSize: 12, color: T.muted }}>{v.graded ? `${v.wins}-${v.losses}` : '—'}</span>
              <span style={{ width: 60, textAlign: 'right', fontFamily: T.mono, fontSize: 12, color: T.green }}>{v.win_pct != null ? pct(v.win_pct) : '—'}</span>
            </div>
          ))}
        </>
      )}
      {Object.keys(byProp).length > 0 && (
        <>
          <SectionLabel>By market</SectionLabel>
          {Object.entries(byProp).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', padding: '9px 2px', borderBottom: `1px solid ${T.border}` }}>
              <span style={{ flex: 1, fontSize: 13, color: T.text }}>{PROP_LABEL[k] || k}</span>
              <span style={{ width: 70, textAlign: 'right', fontFamily: T.mono, fontSize: 12, color: T.muted }}>{v.graded ? `${v.wins}-${v.losses}` : '—'}</span>
              <span style={{ width: 60, textAlign: 'right', fontFamily: T.mono, fontSize: 12, color: T.green }}>{v.win_pct != null ? pct(v.win_pct) : '—'}</span>
            </div>
          ))}
        </>
      )}
      <SectionLabel>Backtest validation</SectionLabel>
      {Object.keys(markets).length === 0
        ? <Empty message="No backtest on record yet." />
        : Object.entries(markets).map(([k, c]) => (
          <div key={k} style={{ display: 'flex', alignItems: 'center', padding: '9px 2px', borderBottom: `1px solid ${T.border}` }}>
            <span style={{ flex: 1, fontSize: 13, color: T.text }}>{PROP_LABEL[k] || k}
              <span style={{ fontSize: 10, color: T.faint }}> · {c.plays || 0} plays</span></span>
            <span style={{ width: 56, textAlign: 'right', fontFamily: T.mono, fontSize: 12, color: T.muted }}>{c.plays ? `${c.hit_rate}%` : '—'}</span>
            <span style={{ width: 74, textAlign: 'right', fontSize: 11, fontWeight: 800, color: VC[c.verdict] || T.faint }}>{c.verdict}</span>
          </div>
        ))}
      <div style={{ marginTop: 16, fontSize: 10.5, color: T.faint, textAlign: 'center' }}>
        Settled results only · research, not betting advice · 21+
      </div>
      <div style={{ height: 16 }} />
    </div>
  )
}

// ── Coming soon (pre-data) ─────────────────────────────────────────────────────
function ComingSoon({ status }) {
  const ready = status?.feed_ready ?? status?.cfbd_key_set
  const steps = [
    ['Space stood up', 'Its own board, tables, routes & access.', true],
    ['Projection models', 'Pass/rush/rec yards, receptions & anytime-TD.', true],
    ['Odds + edges', 'Book lines vs the model — hit % + edge.', true],
    ['Weekly pipeline & track record', 'Auto ingest → project → grade → calibrate.', true],
    ['Data feed', 'ESPN feed connected (free, no quota).', ready],
  ]
  return (
    <div style={{ padding: '24px 18px 40px', maxWidth: 480, margin: '0 auto' }}>
      <div style={{ fontSize: 38 }}>🏈🎓</div>
      <div style={{ fontSize: 24, fontWeight: 900, color: T.text, lineHeight: 1.15, marginTop: 6 }}>
        College football is<br />coming to LINEBRKR
      </div>
      <div style={{ fontSize: 14, color: T.muted, marginTop: 10, lineHeight: 1.5 }}>
        The same prop model that powers NFL, rebuilt for the college game. Fills in as each piece lands.
      </div>
      <div style={{ marginTop: 20 }}>
        {steps.map(([title, sub, done], i) => (
          <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 2px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
            <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
              border: `1.5px solid ${done ? T.green : T.border2}`, color: done ? '#04140D' : T.faint,
              background: done ? T.green : 'transparent', fontSize: 12, fontWeight: 900,
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{done ? '✓' : i + 1}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: done ? T.text : T.muted }}>{title}</div>
              <div style={{ fontSize: 12, color: T.faint, marginTop: 1, lineHeight: 1.4 }}>{sub}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 20, fontSize: 11, color: T.faint, textAlign: 'center' }}>
        Powered by ESPN · Research, not betting advice · 21+
      </div>
    </div>
  )
}

// ── Bottom nav — the NFL BottomNav chrome (icons + labels), CFB's six doors ──────
const CFB_NAV = [['home', 'This Week'], ['board', 'Board', 'grids'], ['touchdowns', 'Touchdowns'],
  ['games', 'Games'], ['parlay', 'Parlay', 'picks'], ['more', 'More']]

export default function CFBApp() {
  const [status, setStatus] = useState(null)
  const [view, setView] = useState('home')
  const [moreSub, setMoreSub] = useState('Record')
  const [week, setWeek] = useState(null)   // defaults to the live week from status
  const [sel, setSel] = useState(null)   // player modal
  const [glossary, setGlossary] = useState(false)
  const [boardInit, setBoardInit] = useState('')   // market to preselect when jumping to the board
  // Record lives at the top of More (NFL layout); old 'record' links land there.
  const goView = v => {
    if (v === 'record') { setMoreSub('Record'); v = 'more' }
    setView(v)
    try { window.scrollTo(0, 0) } catch (_) {}
  }
  const jumpMarket = k => { setBoardInit(k); goView('board') }
  useEffect(() => {
    getCFBStatus().then(st => { setStatus(st); setWeek(w => w ?? (st.week || 1)) })
  }, [])
  const season = status?.season || 2026
  const wk = week || status?.week || 1
  const live = status?.phase === 'live'
  return (
    <div style={{ background: T.bg, minHeight: '100vh', maxWidth: 460, margin: '0 auto',
      fontFamily: "-apple-system,'SF Pro Display',Arial,sans-serif", color: T.text, paddingBottom: 64 }}>
      <Header onGlossary={() => setGlossary(true)} />
      {status == null ? <Spinner />
        : !live ? <ComingSoon status={status} />
        : <>
          <WeekBanner season={season} week={wk} liveWeek={status?.week} setWeek={setWeek} />
          <div style={{ padding: '12px 16px 0' }}>
            {view === 'home' && <HomeTab season={season} week={wk} onPlayer={setSel} setView={goView} onMarket={jumpMarket} />}
            {view === 'board' && <BoardTab season={season} week={wk} onPlayer={setSel} initialProp={boardInit} onInit={() => setBoardInit('')} />}
            {view === 'touchdowns' && <TouchdownsTab season={season} week={wk} onPlayer={setSel} />}
            {view === 'games' && <GamesTab season={season} week={wk} onPlayer={setSel} />}
            {view === 'parlay' && <ParlayTab season={season} week={wk} onPlayer={setSel} />}
            {view === 'more' && <MoreTab status={status} sub={moreSub} setSub={setMoreSub} record={<RecordTab season={season} />} />}
          </div>
        </>}
      {live && <PlayerModal play={sel} season={season} week={wk} onClose={() => setSel(null)} />}
      {glossary && <GlossarySheet onClose={() => setGlossary(false)} />}

      {/* Responsible-gaming footer — same as NFL */}
      <div style={{ padding: '16px 16px 10px', borderTop: `1px solid ${T.border}`, textAlign: 'center' }}>
        <div style={{ fontSize: 11, color: T.muted, lineHeight: 1.5 }}>
          <b style={{ color: T.text }}>21+</b> · Info only, not betting advice · LINEBRKR is a research tool, not a sportsbook.
          <br />Problem gambling? Call <b style={{ color: T.text }}>1-800-GAMBLER</b>.
        </div>
      </div>

      {live && <BottomNav view={view} setView={goView} items={CFB_NAV} />}
    </div>
  )
}
